import axios from './index';

import type { ResType } from '@/typings/common';

/**
 * Types
 */

/** 用户概览 KPI 汇总（GET /v1/stats/users/summary） */
export type UserStatsSummary = {
    total_users: number;
    new_users_7d: number;
    new_users_30d: number;
    creator_count: number;
    creator_ratio: number;
    launcher_count: number;
    launcher_ratio: number;
    dau: number;
    dau_30d_avg: number;
    wau: number;
    mau: number;
    avg_login_count: number;
    avg_active_count: number;
    avg_online_seconds: number;
    stat_date: string | null;
    data_as_of: string | null;
};

export type UserActiveMetric = 'dau' | 'wau' | 'mau';

export type ActiveTrendPoint = {
    date: string;
    value: number;
};

export type UserActiveTrend = {
    metric: UserActiveMetric;
    points: ActiveTrendPoint[];
};

export type UserActiveTrendParams = {
    metric?: UserActiveMetric;
    start?: string;
    end?: string;
};

/**
 * Features overview
 */

/** 功能 key（与后端 FEATURE_KEYS 一致） */
export type FeatureKey = 'register' | 'watch' | 'report' | 'not_interested' | 'publish' | 'launch' | 'buy' | 'sell';

/** 分母指标：注册入口 PV（暂无数据源）/ 注册用户 / 观看用户 */
export type FeatureDenominatorMetric = 'register_pv' | 'registered_users' | 'watch_users';

export type FeatureSummaryItem = {
    feature: FeatureKey;
    participants: number;
    denominator: number | null;
    rate: number | null;
    denominator_metric: FeatureDenominatorMetric;
};

/** 功能概览汇总（GET /v1/stats/features/summary） */
export type FeaturesSummary = {
    /** 起始日期，null 表示全量历史 */
    start: string | null;
    end: string;
    registered_users: number;
    watch_users: number;
    features: FeatureSummaryItem[];
};

export type FeatureDetailItem = {
    user_id: string;
    wallet_address: string | null;
    times: number;
    first_at: string | null;
    last_at: string | null;
};

export type FeatureDetailMeta = {
    page: number;
    size: number;
    total: number;
    more: boolean;
    cursor: string;
};

export type FeatureDetailParams = {
    feature: FeatureKey;
    start?: string;
    end?: string;
    page?: number;
    size?: number;
    sort_by?: 'user_id' | 'times' | 'first_at' | 'last_at';
    sort_order?: 'asc' | 'desc';
};

export type FeatureExportResult = {
    file_name: string;
    path: string;
    public_url: string;
    total: number;
    url: string;
};

/**
 * APIs
 */

/** 用户概览 KPI 汇总 */
export const userStatsSummaryApi = () => {
    return axios<ResType<UserStatsSummary>>('/v1/stats/users/summary', {
        method: 'get',
    });
};

/** 用户 DAU/WAU/MAU 活跃趋势 */
export const userStatsActiveTrendApi = (params?: UserActiveTrendParams) => {
    return axios<ResType<UserActiveTrend>>('/v1/stats/users/active-trend', {
        method: 'get',
        params,
    });
};

/** 功能概览汇总 */
export const featuresSummaryApi = (params?: Pick<FeatureDetailParams, 'start' | 'end'>) => {
    return axios<ResType<FeaturesSummary>>('/v1/stats/features/summary', {
        method: 'get',
        params,
    });
};

/** 功能参与明细（服务端分页） */
export const featureDetailApi = (params: FeatureDetailParams) => {
    return axios<ResType<FeatureDetailItem[]> & { meta?: FeatureDetailMeta }>('/v1/stats/features/detail', {
        method: 'get',
        params,
    });
};

/** 功能参与明细 CSV 导出（返回 S3 签名 URL） */
export const featureDetailExportApi = (params: Omit<FeatureDetailParams, 'page' | 'size'>) => {
    return axios<ResType<FeatureExportResult>>('/v1/stats/features/detail/export', {
        method: 'get',
        params,
    });
};
